import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, TrendingUp, Clock, AlertTriangle, CalendarClock, Gift, RefreshCw, Printer, CheckCircle2 } from "lucide-react";
import { fehlerText } from "@/lib/fehler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EUR = (v) => `${Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const DE = (d) => (d ? d.split("-").reverse().join(".") : "—");
const LB = "text-[10px] uppercase tracking-[0.2em] text-[#BA6D52]";

const Kachel = ({ titel, wert, sub, icon: Icon, akzent, testid }) => (
  <div data-testid={testid}
    className={`border p-6 ${akzent ? "border-[#BA6D52] bg-[#FAF5F1]" : "border-[#E3DECE] bg-[#FAFAF7]"}`}>
    <p className={`${LB} inline-flex items-center gap-2`}>
      {Icon && <Icon size={13} />} {titel}
    </p>
    <p className="font-serif text-3xl md:text-4xl text-[#2C2A26] mt-3 tabular-nums">{wert}</p>
    {sub && <p className="text-xs text-[#757067] mt-2">{sub}</p>}
  </div>
);

export default function AuswertungTab({ pwd }) {
  const [data, setData] = useState(null);
  const [jahr, setJahr] = useState(new Date().getFullYear());
  const [laden, setLaden] = useState(true);
  const [sync, setSync] = useState(false);
  const [druck, setDruck] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);

  const load = useCallback(async () => {
    setLaden(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/finanzen?jahr=${jahr}`, {
        headers: { "X-Admin-Password": pwd },
      });
      setData(d);
    } catch (err) {
      toast.error(fehlerText(err, "Auswertung konnte nicht geladen werden"));
    } finally {
      setLaden(false);
    }
  }, [pwd, jahr]);

  useEffect(() => { load(); }, [load]);

  const lexwareAbgleich = async () => {
    setSync(true);
    setSyncInfo(null);
    try {
      const { data: r } = await axios.post(`${API}/admin/lexware-sync`, {}, {
        headers: { "X-Admin-Password": pwd },
      });
      setSyncInfo(r);
      toast.success(r.bezahlt.length
        ? `${r.bezahlt.length} Rechnung${r.bezahlt.length !== 1 ? "en" : ""} als bezahlt übernommen`
        : `${r.geprueft} offene Rechnung${r.geprueft !== 1 ? "en" : ""} geprüft — noch kein neuer Zahlungseingang`,
        { duration: 7000 });
      await load();
    } catch (err) {
      toast.error(fehlerText(err, "Abgleich mit Lexware fehlgeschlagen"));
    } finally {
      setSync(false);
    }
  };

  const abhaken = async (p) => {
    if (!window.confirm(
      `„${p.kunde}" aus der Vorschau nehmen?\n\nNutze das, wenn dieser Einsatz über Stunden oder `
      + "eine andere Rechnung bereits abgerechnet wurde. Der Auftrag bleibt erhalten, zählt aber "
      + "nicht mehr zum erwarteten Geldeingang.")) return;
    try {
      await axios.post(`${API}/admin/finanzen/beauftragt/${p.art}/${p.id}/abgerechnet`, {},
        { headers: { "X-Admin-Password": pwd } });
      toast.success("Aus der Vorschau genommen");
      await load();
    } catch (err) {
      toast.error(fehlerText(err, "Konnte nicht abgehakt werden"));
    }
  };

  const ausdruck = async () => {
    setDruck(true);
    try {
      const { data: blob } = await axios.get(`${API}/admin/finanzen/pdf?jahr=${jahr}`, {
        headers: { "X-Admin-Password": pwd }, responseType: "blob",
      });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(fehlerText(err, "Ausdruck konnte nicht erstellt werden"));
    } finally {
      setDruck(false);
    }
  };

  if (laden && !data) {
    return <div className="py-20 text-center"><Loader2 className="animate-spin mx-auto text-[#BA6D52]" /></div>;
  }
  if (!data) return null;

  const s = data.summary;
  const maxMonat = Math.max(1, ...data.monate.map((m) => m.abgerechnet_netto + m.beauftragt_netto));

  return (
    <div className="space-y-8" data-testid="auswertung-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className={LB}>Auswertung · alle Beträge netto</p>
          <h2 className="font-serif text-2xl md:text-3xl text-[#2C2A26] mt-2">
            Was reingekommen ist — und was noch kommt
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={ausdruck} disabled={druck} data-testid="auswertung-pdf"
            className="inline-flex items-center gap-2 bg-[#BA6D52] hover:bg-[#9C4F31] text-white disabled:opacity-60 px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] transition-colors">
            {druck ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}
            Ausdruck erstellen
          </button>
          <button onClick={lexwareAbgleich} disabled={sync} data-testid="auswertung-lexware-sync"
            className="inline-flex items-center gap-2 border border-[#BA6D52] text-[#BA6D52] hover:bg-[#FAF5F1] disabled:opacity-60 px-4 py-2.5 text-[11px] uppercase tracking-[0.15em]">
            {sync ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Mit Lexware abgleichen
          </button>
          <select value={jahr} onChange={(e) => setJahr(Number(e.target.value))}
            data-testid="auswertung-jahr"
            className="border border-[#E3DECE] bg-white px-4 py-2.5 text-sm focus:outline-none focus:border-[#BA6D52]">
            {(data.jahre || []).map((j) => <option key={j} value={j}>{j}</option>)}
          </select>
        </div>
      </div>

      {syncInfo && (
        <div className="border border-[#E3DECE] bg-[#FAFAF7] px-4 py-3 text-sm" data-testid="auswertung-sync-info">
          <p className="text-[#2C2A26]">
            {syncInfo.geprueft} offene Rechnung{syncInfo.geprueft !== 1 ? "en" : ""} bei Lexware geprüft ·{" "}
            {syncInfo.bezahlt.length} als bezahlt übernommen
            {syncInfo.storniert.length > 0 ? ` · ${syncInfo.storniert.length} in Lexware storniert` : ""}
            {syncInfo.fehler.length > 0 ? ` · ${syncInfo.fehler.length} nicht abrufbar` : ""}
          </p>
          {syncInfo.bezahlt.length > 0 && (
            <p className="text-xs text-[#4F6B3E] mt-2">
              {syncInfo.bezahlt.map((b) => `${b.nummer} ${b.kunde} (${DE(b.bezahlt_am)})`).join(" · ")}
            </p>
          )}
          {syncInfo.storniert.length > 0 && (
            <p className="text-xs text-[#A6543F] mt-2">
              In Lexware storniert — bitte im Portal prüfen:{" "}
              {syncInfo.storniert.map((b) => `${b.nummer} ${b.kunde}`).join(" · ")}
            </p>
          )}
          {syncInfo.fehler.length > 0 && (
            <p className="text-xs text-[#757067] mt-2">
              Nicht abrufbar: {syncInfo.fehler.map((b) => `${b.nummer || "?"} ${b.kunde}`).join(" · ")}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kachel akzent testid="auswertung-abgerechnet" icon={TrendingUp}
          titel={`Abgerechnet ${data.jahr}`} wert={EUR(s.abgerechnet_netto)}
          sub={`davon ${EUR(s.bezahlt_netto)} als bezahlt markiert`} />
        <Kachel testid="auswertung-offen" icon={Clock}
          titel="Noch offen" wert={EUR(s.offen_netto)}
          sub={`${s.offen_count} Rechnung${s.offen_count !== 1 ? "en" : ""} noch nicht bezahlt`} />
        <Kachel testid="auswertung-ueberfaellig" icon={AlertTriangle}
          titel="Davon überfällig" wert={EUR(s.ueberfaellig_netto)}
          sub={s.ueberfaellig_netto > 0 ? "Zahlungsziel überschritten" : "alles im Rahmen"} />
        <Kachel testid="auswertung-beauftragt" icon={CalendarClock}
          titel="Fest gebucht, kommt noch" wert={EUR(s.beauftragt_netto)}
          sub={s.beauftragt_count === 1 ? "1 verbindlich bestätigter Auftrag"
            : `${s.beauftragt_count} verbindlich bestätigte Aufträge`} />
      </div>

      {/* Monatsübersicht */}
      <div className="border border-[#E3DECE] bg-white p-5" data-testid="auswertung-monate">
        <p className={LB}>Monat für Monat · {data.jahr}</p>
        <div className="mt-4 space-y-2">
          {data.monate.map((m) => (
            <div key={m.monat} className="grid grid-cols-12 gap-3 items-center text-sm py-1"
              data-testid={`auswertung-monat-${m.monat}`}>
              <span className="col-span-3 md:col-span-2 text-[#757067]">{m.name}</span>
              <div className="col-span-5 md:col-span-6 h-2 bg-[#F4EFE6] relative">
                <div className="absolute inset-y-0 left-0 bg-[#BA6D52]"
                  style={{ width: `${(m.abgerechnet_netto / maxMonat) * 100}%` }} />
                {m.beauftragt_netto > 0 && (
                  <div className="absolute inset-y-0 border border-dashed border-[#BA6D52] bg-[#BA6D52]/15"
                    style={{ left: `${(m.abgerechnet_netto / maxMonat) * 100}%`,
                      width: `${(m.beauftragt_netto / maxMonat) * 100}%` }} />
                )}
                <div className="absolute inset-y-0 left-0 bg-[#4F6B3E] opacity-70"
                  style={{ width: `${(m.bezahlt_netto / maxMonat) * 100}%`, height: "4px", top: "6px" }} />
              </div>
              <span className="col-span-4 md:col-span-4 text-right tabular-nums whitespace-nowrap">
                {EUR(m.abgerechnet_netto)}
                {m.beauftragt_netto > 0 && (
                  <span className="text-[#BA6D52] text-xs"> + {EUR(m.beauftragt_netto)} gebucht</span>
                )}
                {m.gutscheine_netto > 0 && (
                  <span className="text-[#757067] text-xs"> · {EUR(m.gutscheine_netto)} Gutscheine</span>
                )}
              </span>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-[#9A958C] mt-4">
          Voller Balken = abgerechnet · dünner grüner Strich = davon bezahlt · gestrichelt = fest
          gebucht, Rechnung folgt nach dem Einsatz. Gutscheinverkäufe stehen separat, weil sie erst
          beim Einlösen zu einem Einsatz werden.
        </p>
      </div>

      {/* Was noch reinkommt */}
      <div className="border border-[#E3DECE] bg-[#FAFAF7] p-5" data-testid="auswertung-faellig">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <p className={LB}>Das kommt noch rein</p>
          <p className="text-xs text-[#757067]">
            {EUR(data.erwartet.aus_rechnungen)} aus gestellten Rechnungen ·{" "}
            {EUR(data.erwartet.aus_auftraegen)} aus fest gebuchten Einsätzen
          </p>
        </div>
        <p className="font-serif text-3xl md:text-4xl text-[#2C2A26] mt-2 tabular-nums"
          data-testid="auswertung-erwartet-gesamt">{EUR(data.erwartet.gesamt)}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 text-sm">
          <div><p className="text-[#757067] text-xs">Überfällig</p>
            <p className="font-serif text-xl mt-1 text-[#A6543F]">{EUR(data.erwartet.ueberfaellig)}</p></div>
          <div><p className="text-[#757067] text-xs">Diesen Monat</p>
            <p className="font-serif text-xl mt-1">{EUR(data.erwartet.dieser_monat)}</p></div>
          <div><p className="text-[#757067] text-xs">Nächsten Monat</p>
            <p className="font-serif text-xl mt-1">{EUR(data.erwartet.naechster_monat)}</p></div>
          <div><p className="text-[#757067] text-xs">Später</p>
            <p className="font-serif text-xl mt-1">{EUR(data.erwartet.spaeter)}</p></div>
        </div>

        {data.offene_rechnungen.length > 0 && (
          <div className="mt-6">
            <p className={LB}>Rechnung gestellt, Zahlung offen</p>
            <div className="mt-2 divide-y divide-[#EFE9DC] border-t border-[#EFE9DC]">
              {data.offene_rechnungen.map((p) => (
                <div key={`${p.art}-${p.id}`} className="py-3 flex justify-between gap-4 flex-wrap text-sm"
                  data-testid={`auswertung-offen-${p.nummer}`}>
                  <span>
                    <span className="font-mono text-xs text-[#BA6D52]">{p.nummer}</span>
                    <span className="text-[#2C2A26]"> · {p.kunde}</span>
                    <span className="text-[#757067]">
                      {" · "}{p.art === "sammel" ? "Sammelrechnung" : "Rechnung"} vom {DE(p.datum)}
                      {p.faellig_am ? ` · zahlbar bis ${DE(p.faellig_am)}` : ""}
                    </span>
                    {p.ueberfaellig && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#A6543F]">überfällig</span>
                    )}
                  </span>
                  <span className="tabular-nums whitespace-nowrap">{EUR(p.netto)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.beauftragt.length > 0 && (
          <div className="mt-6" data-testid="auswertung-beauftragt-liste">
            <p className={LB}>Fest gebucht · Rechnung folgt nach dem Einsatz</p>
            <div className="mt-2 divide-y divide-[#EFE9DC] border-t border-[#EFE9DC]">
              {data.beauftragt.map((p) => (
                <div key={`${p.art}-${p.id}`} className="py-3 flex justify-between gap-4 flex-wrap text-sm"
                  data-testid={`auswertung-beauftragt-${p.nummer || p.id}`}>
                  <span>
                    <span className="font-mono text-xs text-[#BA6D52]">{p.nummer || "—"}</span>
                    <span className="text-[#2C2A26]"> · {p.kunde}</span>
                    <span className="text-[#757067]">
                      {" · "}Einsatz {DE(p.einsatz)}
                      {p.personen > 0 ? ` · ${p.personen} Pers.` : ""}
                    </span>
                    {p.einsatz_vorbei && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#A6543F]">
                        Einsatz vorbei — Rechnung erstellen
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-3 whitespace-nowrap">
                    <span className="tabular-nums">{EUR(p.netto)}</span>
                    <button onClick={() => abhaken(p)} data-testid={`auswertung-abhaken-${p.nummer || p.id}`}
                      title="Wurde über Stunden oder separat abgerechnet — aus der Vorschau nehmen"
                      className="text-[#9A958C] hover:text-[#A6543F] transition-colors">
                      <CheckCircle2 size={15} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.angebote_offen.length > 0 && (
          <div className="mt-6" data-testid="auswertung-angebote-liste">
            <p className={LB}>Angebot draußen · Kunde hat noch nicht entschieden</p>
            <p className="text-xs text-[#757067] mt-1">
              {EUR(s.angebote_offen_netto)} aus {s.angebote_offen_count} Angebot
              {s.angebote_offen_count !== 1 ? "en" : ""} — noch unverbindlich, deshalb oben nicht mitgezählt
            </p>
            <div className="mt-3 divide-y divide-[#EFE9DC] border-t border-[#EFE9DC]">
              {data.angebote_offen.map((p) => (
                <div key={`${p.art}-${p.id}`} className="py-3 flex justify-between gap-4 flex-wrap text-sm"
                  data-testid={`auswertung-angebot-${p.nummer || p.id}`}>
                  <span>
                    <span className="font-mono text-xs text-[#BA6D52]">{p.nummer || "—"}</span>
                    <span className="text-[#2C2A26]"> · {p.kunde}</span>
                    <span className="text-[#757067]">
                      {" · "}Einsatz {DE(p.einsatz)}
                      {p.gueltig_bis ? ` · gültig bis ${DE(p.gueltig_bis)}` : ""}
                    </span>
                    {p.rueckmeldung && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-[#A6543F]">
                        Änderungswunsch offen
                      </span>
                    )}
                  </span>
                  <span className="tabular-nums whitespace-nowrap text-[#757067]">{EUR(p.netto)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data.offene_rechnungen.length === 0 && data.beauftragt.length === 0 && (
          <p className="text-sm text-[#757067] mt-5">Alles bezahlt, nichts mehr offen.</p>
        )}
      </div>

      {/* Gutscheine */}
      {(data.gutscheine || []).length > 0 && (
        <div className="border border-[#E3DECE] bg-white p-5" data-testid="auswertung-gutscheine">
          <p className={`${LB} inline-flex items-center gap-2`}><Gift size={13} /> Gutscheine {data.jahr}</p>
          <p className="font-serif text-2xl mt-2 tabular-nums">{EUR(s.gutscheine_netto)}</p>
          <p className="text-xs text-[#757067] mt-1">
            {data.gutscheine.length} verkaufte{data.gutscheine.length === 1 ? "r" : ""} Gutschein
            {data.gutscheine.length !== 1 ? "e" : ""} · netto aus dem Verkaufspreis gerechnet
          </p>
        </div>
      )}

      <p className="text-[11px] text-[#9A958C]">
        Stand {DE(data.stand)} · „Abgerechnet" zählt nach Rechnungsdatum, „bezahlt" nach
        Lexware-Abgleich bzw. deiner Bestätigung im Portal. Testrechnungen und Stornos sind nicht enthalten. Für die endgültige
        BWA bleibt dein Steuerbüro bzw. Lexware maßgeblich.
      </p>
    </div>
  );
}
